"use client";

import { useState } from "react";
import { TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { StyledResult } from "@/components/tools/text/styled-result";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  applyStyle,
  MONOSPACE_MAP,
  toMarkdownCode,
  unstyledCharacters,
} from "@/lib/tool-kit/text/unicode-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const MONOSPACE_TEXT_GENERATOR_STRINGS = {
  inputLabel: "Your text",
  inputPlaceholder: "Type or paste the text you want in monospace",
  trySample: "Try a sample",
  sample: 'print("Hello, world!") # 2026',
  clear: "Clear",
  results: "Results",
  monospaceLabel: "Monospace Unicode (𝚖𝚘𝚗𝚘𝚜𝚙𝚊𝚌𝚎)",
  monospaceHint:
    "Plain-text characters: they paste into bios, display names, posts and comments where no formatting is allowed.",
  codeLabel: "Code formatting for Markdown, Discord and Slack",
  codeHint:
    "Real formatting instead of look-alike characters. In an app that reads Markdown this shows in a true monospace font, and screen readers and search read it normally.",
  emptyResult: "Type above to see the result.",
  copy: "Copy",
  copied: "Copied!",
  unchanged:
    "Left as they were — Unicode has no monospace version of these: {list}",
  accessibility:
    'Screen readers may announce each letter as a math symbol ("mathematical monospace small h") or skip it, and search, spell check and translation treat these characters as different from ordinary letters.',
} as const;

export default function MonospaceTextGeneratorWidget() {
  const strings = useToolUi(MONOSPACE_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const styled = applyStyle(input, MONOSPACE_MAP);
  const unchanged = unstyledCharacters(input, MONOSPACE_MAP);

  return (
    <>
      <TextAreaField
        id="mono-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        prose
        rows={4}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StyledResult
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyText={strings.emptyResult}
          hint={strings.monospaceHint}
          label={strings.monospaceLabel}
          value={styled}
        />
        {unchanged.length > 0 ? (
          <p className={HINT}>
            {t(strings.unchanged, { list: unchanged.join(" ") })}
          </p>
        ) : null}
        <StyledResult
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyText={strings.emptyResult}
          hint={strings.codeHint}
          label={strings.codeLabel}
          value={toMarkdownCode(input)}
        />
        <p className={HINT}>{strings.accessibility}</p>
      </section>
    </>
  );
}
